/**
 * selectionFingerprint — IDENTITY of a selection, not merely its SIZE.
 *
 * ⛔⛔ WHY THIS EXISTS. The "(all products)" badge compared the current selection's LENGTH with the
 * length select-all produced. Length equality is not identity: replace one selected product with
 * another and the count is restored, so the badge reappears over a set that is no longer what
 * select-all returned. Changing the filter was worse — the old selection and its snapshot went on
 * claiming "all products" for a view they had nothing to do with.
 *
 * 🔑 ORDER-INDEPENDENT ON PURPOSE. The hook rebuilds its array on every mutation, so an
 * order-sensitive digest would invalidate the badge on a no-op reorder and teach everyone to
 * distrust it. What matters is WHICH products, not the sequence they happen to sit in — so each id
 * contributes independently and the contributions are summed.
 *
 * ⚠️ CHEAP BY CONSTRUCTION, because select-all can hold 500,000 ids: one pass, integer arithmetic,
 * no sort and no giant intermediate string. Callers memoise on the array reference so it recomputes
 * only when the selection actually changes.
 */
export function fingerprintSelection(ids: readonly string[]): string {
  let acc = 0;
  for (const id of ids) {
    // FNV-1a over the id, then folded into an order-independent accumulator.
    let h = 0x811c9dc5;
    for (let i = 0; i < id.length; i += 1) {
      h ^= id.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
    acc = (acc + (h >>> 0)) >>> 0;
  }
  // ⇒ The LENGTH travels too. A hash collision is unlikely; a collision that ALSO preserves the
  // count is far less likely still, and the count is free.
  return `${ids.length}:${acc.toString(16)}`;
}
